<template>
  <div class="task-video-cards">
    <div class="row">
      <div
        class="col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4"
        v-for="task in tasks"
        :key="task.id"
      >
        <div class="card task-card" @click="handleCardClick(task)">
          <div class="video-thumbnail-container">
            <!-- YouTube thumbnail or default image -->
            <img
              :src="getCardImage(task)"
              class="card-img wd-100p"
              :alt="task.task"
            />
            <!-- Play button overlay for YouTube videos -->
            <div v-if="isYouTubeUrl(task.st3)" class="play-button-overlay">
              <div class="play-button">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                  <circle cx="30" cy="30" r="30" fill="rgba(0,0,0,0.7)" />
                  <polygon points="22,15 22,45 45,30" fill="white" />
                </svg>
              </div>
            </div>
          </div>
          <div class="card-body">
            <h5 class="card-title text-truncate text-uppercase">{{ task.task }}</h5>
            <div class="text-orange" v-if="task.createdDate">
              {{ task.createdDate | moment("YYYY.MM.DD") }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- YouTube Video Modal -->
    <div 
      v-if="showVideoModal" 
      class="video-modal-overlay"
      @click="closeVideoModal"
    >
      <div class="video-modal-container" @click.stop>
        <div class="video-modal-header">
          <h3>{{ currentTask?.task }}</h3>
          <button @click="closeVideoModal" class="close-button">&times;</button>
        </div>
        <div class="video-modal-content">
          <div class="embed-responsive embed-responsive-16by9">
            <iframe
              v-if="currentVideoId"
              width="100%"
              height="100%"
              :src="`https://www.youtube.com/embed/${currentVideoId}?autoplay=1`"
              title="YouTube video player"
              frameborder="0"
              allowfullscreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TaskVideoCards",
  props: {
    tasks: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showVideoModal: false,
      currentTask: null,
      currentVideoId: null
    };
  },
  methods: {
    isYouTubeUrl(url) {
      if (!url) return false;
      const youtubeRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
      return youtubeRegex.test(url);
    },

    getYouTubeId(url) {
      if (!url) return null;
      const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      return match ? match[1] : null;
    },

    getCardImage(task) {
      if (this.isYouTubeUrl(task.st3)) {
        const videoId = this.getYouTubeId(task.st3);
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      }
      return task.defaultAttachmentUrl
        ? `${task.defaultAttachmentUrl}?w=460&h=360&mode=crop`
        : "";
    },

    handleCardClick(task) {
      if (this.isYouTubeUrl(task.st3)) {
        this.currentTask = task;
        this.currentVideoId = this.getYouTubeId(task.st3);
        this.showVideoModal = true;
        // Prevent body scroll when modal is open
        document.body.style.overflow = "hidden";
      } else {
        // Handle non-YouTube content (you can emit an event or navigate)
        this.$emit("task-clicked", task);
      }
    },

    closeVideoModal() {
      this.showVideoModal = false;
      this.currentTask = null;
      this.currentVideoId = null;
      // Restore body scroll
      document.body.style.overflow = "auto";
    }
  },

  beforeDestroy() {
    // Ensure body scroll is restored if component is destroyed while modal is open
    document.body.style.overflow = "auto";
  }
};
</script>

<style scoped>
.task-video-cards {
  padding: 20px 0;
}

.task-card {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.task-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.video-thumbnail-container {
  position: relative;
  overflow: hidden;
}

.card-img {
  height: 250px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.task-card:hover .card-img {
  transform: scale(1.05);
}

.play-button-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.task-card:hover .play-button-overlay {
  opacity: 1;
}

.play-button {
  transition: transform 0.2s ease;
}

.task-card:hover .play-button {
  transform: scale(1.1);
}

.card-body {
  padding: 15px;
  background: white;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.text-orange {
  color: #ff6b35;
  font-size: 14px;
}

/* Video Modal Styles */
.video-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.video-modal-container {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.video-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.video-modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
  flex: 1;
  padding-right: 20px;
}

.close-button {
  background: none;
  border: none;
  font-size: 28px;
  font-weight: bold;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.close-button:hover {
  color: #333;
  background-color: #e9ecef;
}

.video-modal-content {
  padding: 0;
}

.embed-responsive {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  overflow: hidden;
}

.embed-responsive-16by9 {
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
}

.embed-responsive iframe {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  height: 100%;
  width: 100%;
  border: 0;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .video-modal-overlay {
    padding: 10px;
  }
  
  .video-modal-container {
    max-height: 95vh;
  }
  
  .video-modal-header {
    padding: 10px 15px;
  }
  
  .video-modal-header h3 {
    font-size: 16px;
  }
  
  .close-button {
    font-size: 24px;
    width: 25px;
    height: 25px;
  }
}

@media (max-width: 576px) {
  .col-xs-12 {
    padding: 0 10px;
  }
  
  .task-card {
    margin-bottom: 15px;
  }
  
  .card-img {
    height: 200px;
  }
}
</style>
